import { useState, useEffect } from "react";
import {
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonImg,
  IonHeader,
  IonMenuButton,
  IonAccordion,
  IonAccordionGroup,
} from "@ionic/react";

import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  businessOutline,
  businessSharp,
  barChartOutline,
  barChartSharp,
  logOutOutline,
  logOutSharp,
  walletOutline,
  walletSharp,
  closeOutline,
  closeSharp,
  receiptOutline,
  receiptSharp,
  flashSharp,
  flashOutline,
  personOutline,
  personSharp,
  homeOutline,
  homeSharp,
  settingsOutline,
  settingsSharp,
  notificationsOutline,
  notificationsSharp,
  peopleOutline,
  peopleSharp,
  cardOutline,
  cardSharp,
} from "ionicons/icons";

import "./Menu.css";
import { OCCUPANT_TYPE, UNIFORM_RESOURCE_LOCATORS } from "../utils/constants";
import _ from "lodash";

import * as PermChecker from "../utils/PermUtils";

interface AppPage {
  url: string;
  iosIcon: string;
  mdIcon: string;
  title: string;
  children?: AppPage[];
}

const APP_PAGES: AppPage[] = [
  {
    title: "Dashboard",
    url: UNIFORM_RESOURCE_LOCATORS.dashboard,
    iosIcon: barChartOutline,
    mdIcon: barChartSharp,
  },
  {
    title: "Financials",
    url: "",
    iosIcon: "",
    mdIcon: "",
    children: [
      {
        title: "Payments",
        url: UNIFORM_RESOURCE_LOCATORS.payments,
        iosIcon: walletOutline,
        mdIcon: walletSharp,
      },
      {
        title: "Invoices",
        url: UNIFORM_RESOURCE_LOCATORS.invoices,
        iosIcon: receiptOutline,
        mdIcon: receiptSharp,
      },
    ],
  },
  {
    title: "Tenants",
    url: "",
    iosIcon: "",
    mdIcon: "",
    children: [
      {
        title: "Tenants",
        url: UNIFORM_RESOURCE_LOCATORS.tenants,
        iosIcon: personOutline,
        mdIcon: personSharp,
      },
      {
        title: "Utilities",
        url: UNIFORM_RESOURCE_LOCATORS.utilities,
        iosIcon: flashOutline,
        mdIcon: flashSharp,
      },
      {
        title: "Units",
        url: UNIFORM_RESOURCE_LOCATORS.units,
        iosIcon: homeOutline,
        mdIcon: homeSharp,
      },
      {
        title: "Properties",
        url: UNIFORM_RESOURCE_LOCATORS.properties,
        iosIcon: businessOutline,
        mdIcon: businessSharp,
      },
    ],
  },
  {
    title: "Reports",
    url: "",
    iosIcon: barChartOutline,
    mdIcon: barChartSharp,
    children: [
      {
        title: "Reports",
        url: UNIFORM_RESOURCE_LOCATORS.reports,
        iosIcon: barChartOutline,
        mdIcon: barChartSharp,
      }
    ]
  },
  {
    title: "Settings",
    url: "",
    iosIcon: "",
    mdIcon: "",
    children: [
      {
        title: "General",
        url: UNIFORM_RESOURCE_LOCATORS.generalSettings,
        iosIcon: settingsOutline,
        mdIcon: settingsSharp,
      },
      {
        title: "Alerts",
        url: UNIFORM_RESOURCE_LOCATORS.alertSettings,
        iosIcon: notificationsOutline,
        mdIcon: notificationsSharp,
      },
      {
        title: "Account Info",
        url: UNIFORM_RESOURCE_LOCATORS.accountInfo,
        iosIcon: personOutline,
        mdIcon: personSharp,
      },
      {
        title: "Team",
        url: UNIFORM_RESOURCE_LOCATORS.team,
        iosIcon: peopleOutline,
        mdIcon: peopleSharp,
      },
      {
        title: "Billing",
        url: UNIFORM_RESOURCE_LOCATORS.billing,
        iosIcon: cardOutline,
        mdIcon: cardSharp,
      },
    ],
  },

  {
    title: "Logout",
    url: UNIFORM_RESOURCE_LOCATORS.appLogin,
    iosIcon: logOutOutline,
    mdIcon: logOutSharp,
  },
];

const Menu: React.FC = () => {
  const location = useLocation();
  const userPermissions = useSelector(
    (state: any) => state.options.userPermissions
  );

  const options = useSelector((state: any) => state.options);

  const [appPages, setAppPages] = useState(APP_PAGES);
  useEffect(() => {
    if (!_.isEmpty(userPermissions)) {
      for (let page of appPages) {
        for (let child of page.children || []) {
          if (PermChecker.PAGES_TO_CHECK.includes(child.url)) {
            let childHasPerm = PermChecker.checkUserHasPermission(
              PermChecker.URL_MODEL_PERM_MAP[child.url].model,
              PermChecker.URL_MODEL_PERM_MAP[child.url].perm,
              userPermissions
            );
            if (!childHasPerm) {
              page.children = page.children?.filter((c) => c.url !== child.url);
            }
          }
        }
        if (!page.children && PermChecker.PAGES_TO_CHECK.includes(page.url)) {
          let pageHasPerm = PermChecker.checkUserHasPermission(
            PermChecker.URL_MODEL_PERM_MAP[page.url].model,
            PermChecker.URL_MODEL_PERM_MAP[page.url].perm,
            userPermissions
          );
          if (!pageHasPerm) {
            setAppPages(appPages.filter((p) => p.url !== page.url));
          }
        }
      }
    }

  }, [userPermissions])


  const renderMenuItem = (appPage: AppPage) => {
    if (appPage.children) {
      return (
        <IonAccordionGroup key={appPage.title}>
          <IonAccordion value="first">
            <IonItem
              slot="header"
              className={""}
              onClick={() => {}}
              routerDirection="none"
              lines="none"
              detail={false}
            >
              <IonLabel>{appPage.title === "Tenants" ? options.userAccountType === OCCUPANT_TYPE? "Tenants": "Members" : appPage.title}</IonLabel>
            </IonItem>
            <div slot="content">
              {appPage.children && (
                <IonList style={{ margin: 0, padding: 0 }}>
                  {appPage.children.map((child, index) => {
                    return (
                      <IonMenuToggle key={child.title} autoHide={false}>
                        <IonItem
                          className={
                            location.pathname === child.url
                              ? "selected pl-4"
                              : "pl-4"
                          }
                          routerLink={child.url}
                          routerDirection="none"
                          lines="none"
                          detail={false}
                        >
                          <IonIcon
                            slot="start"
                            ios={child.iosIcon}
                            md={child.mdIcon}
                          />
                          <IonLabel>{child.title === "Tenants" ? options.userAccountType === OCCUPANT_TYPE ? "Tenants": "Members" : child.title}</IonLabel>
                        </IonItem>
                      </IonMenuToggle>
                    );
                  })}
                </IonList>
              )}
            </div>
          </IonAccordion>
        </IonAccordionGroup>
      );
    } else {
      return (
        <IonMenuToggle key={appPage.title} autoHide={false}>
          <IonItem
            className={location.pathname === appPage.url ? "selected" : ""}
            routerLink={appPage.url}
            routerDirection="none"
            lines="none"
            detail={false}
          >
            <IonIcon slot="start" ios={appPage.iosIcon} md={appPage.mdIcon} />
            <IonLabel>{appPage.title}</IonLabel>
          </IonItem>
        </IonMenuToggle>
      );
    }
  };
  return (
    <IonMenu contentId="main" type="overlay">
      <IonHeader
        className="ion-no-border"
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          alignItems: "center",
        }}
      >
        <IonMenuButton>
          <IonIcon slot="close" ios={closeOutline} md={closeSharp} />
        </IonMenuButton>
        <IonImg
          src="images/logos/BOMAHUT-purple.svg"
          style={{ maxWidth: "200px", maxHeight: "75px" }}
          alt="Bomahut Logo"
          class="ion-margin"
        ></IonImg>
      </IonHeader>

      <IonContent>
        <IonList
          style={{ padding: "0px" }}
          className="ion-no-padding ion-no-margin"
          id="menu-list"
        >
          {appPages.map((appPage, index) => {
            return renderMenuItem(appPage);
          })}
        </IonList>
      </IonContent>
    </IonMenu>
  );
};

export default Menu;
